import React from "react";
import { t } from "i18next";
import { AnimatePresence, motion } from "framer-motion";
import Proximiio from "proximiio-js-library";
import Feature from "proximiio-js-library/lib/models/feature";
import {
  osName,
  browserName,
  mobileModel,
  mobileVendor,
  deviceType,
} from "react-device-detect";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { FilterItemModel } from "@/models/filterItem.model";
import { cn, filterByAmenity, getSortedPOIs } from "@/lib/utils";
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

import useMapStore from "@/store/mapStore";
import useFeaturesStore from "@/store/featuresStore";
import { Button } from "./ui/button";
import { PiXBold } from "react-icons/pi";
import { filterCategories } from "@/store/data";

interface FilterPickerProps {
  heading: string;
  id: string;
  color?: string;
  items: FilterItemModel[];
  isOpen?: boolean;
}

function FilterPicker({
  heading,
  id,
  color,
  items,
  isOpen,
}: FilterPickerProps) {
  const kioskMode = useMapStore((state) => state.kioskMode);
  const gpsMode = useMapStore((state) => state.gpsMode);
  const map = useMapStore((state) => state.map);
  const activeKiosk = useMapStore((state) => state.activeKiosk);
  const currentLang = useMapStore((state) => state.currentLang);
  const appSession = useMapStore((state) => state.appSession);
  const features = useFeaturesStore((state) => state.features);
  const activeFilter = useMapStore((state) => state.activeFilter);
  const floors = useMapStore((state) => state.floors);
  const currentFloor = useMapStore((state) => state.currentFloor);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const activeCategory = useMapStore((state) => state.activeCategory);
  const setActiveFilter = useMapStore((state) => state.setActiveFilter);
  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const setActiveMenuItem = useMapStore((state) => state.setActiveMenuItem);
  const centerToFinish = useMapStore((state) => state.centerToFinish);

  const pois = getSortedPOIs({ features, currentLang, floors });
  const category = filterCategories.find((i) => i.id === id);

  const filterClickHandler = (item: FilterItemModel) => {
    if (item.type === "closest") {
      if (!kioskMode && !gpsMode) {
        item.type = "list";
      }
    }
    const filteredPois = filterByAmenity(pois, item.id);

    if (item.title === activeFilter.title) {
      setActiveFilter({} as FilterItemModel);
      return;
    }

    if (Object.keys(map).length > 0) {
      /*map.setFeaturesHighlight(
        filteredPois.map((i) => i.id),
        "#336eae",
        58,
        0.2,
        true,
      );*/
      if ((!gpsMode && !kioskMode) || item.type !== "closest") {
        if (item.type === "single") {
          const feature = features.find((f) => f.id === item.featureId)!;
          setRouteFinish(feature);
          centerToFinish();
        }
        if (filteredPois.length === 1) {
          const feature = features.find((f) => f.id === filteredPois[0].id)!;
          setRouteFinish(feature);
          centerToFinish();
        }
        if (filteredPois.length > 1) {
          const sameLevelFeature = filteredPois.find(
            (i) => i.properties.level === currentFloor.level,
          );
          if (!sameLevelFeature) {
            const floor = floors.find(
              (f) => f.level === filteredPois[0].properties.level,
            );
            if (floor) {
              map.setFloorById(floor.id);
            }
          }
          if (!mobileMode) {
            map.getMapboxInstance().jumpTo({
              center: sameLevelFeature
                ? (sameLevelFeature.geometry.coordinates as [number, number])
                : (filteredPois[0].geometry.coordinates as [number, number]),
            });
          }
        }
        setActiveFilter(item);
      } else {
        const closestFeature = map.getClosestFeature(
          item.id as string,
        ) as Feature;
        // setRouteStart("kiosk");
        setActiveFilter(item);
        setRouteFinish(closestFeature);
        centerToFinish();
      }

      if (item.listed !== false) {
        //save amenity select log
        console.log("LOG: save amenity select log");
        const userData = {
          osName,
          browserName,
          mobileModel,
          mobileVendor,
          deviceType:
            kioskMode && !mobileMode && activeKiosk?.name
              ? activeKiosk?.name
              : deviceType,
        };
        new Proximiio.InteractionLogger({
          interactionType: "select",
          targetElementType: "amenity",
          targetElementTitle: item.translatedTitle
            ? item.translatedTitle
            : t(item.title, { lng: "en" }),
          targetElementId: Array.isArray(item.id)
            ? item.id.join("_")
            : item.id
              ? item.id
              : undefined,
          targetElementAmenityCategory: activeCategory
            ? t(activeCategory.title, { lng: "en" })
            : undefined,
          kioskId: activeKiosk?.id ? activeKiosk?.id : undefined,
          kioskTitle: activeKiosk?.name ? activeKiosk?.name : undefined,
          metadata: userData,
          language: currentLang,
          session: appSession,
        });
      }
    }
  };

  const closeHandler = () => {
    setActiveMenuItem("map");
    setActiveFilter({} as FilterItemModel);
    setTimeout(() => {
      map.setFeaturesHighlight([]);
    }, 1000);
  };

  const filterItems = () => {
    return items
      .filter((i) => i.listed !== false)
      .map((item, idx) => {
        const filteredPois = filterByAmenity(pois, item.id);
        if (filteredPois.length === 0) return null;
        return (
          <motion.li
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 40,
              delay: 0.1 + idx / 30,
            }}
            key={idx}
            className={cn(
              "group flex cursor-pointer items-center gap-3 rounded-md py-1.5 capitalize hover:text-primary",
              color &&
                "hover:text-[var(--category-color)] hover:text-opacity-5",
              item.color &&
                "hover:text-[var(--item-color)] hover:text-opacity-5",
            )}
            style={{
              // @ts-expect-error: custom property
              "--item-color": `hsl(${item.color})`,
            }}
            onClick={() => filterClickHandler(item)}
          >
            <span
              className={cn(
                "flex min-w-6 text-2xl text-primary lg:min-w-10",
                color && "bg-opacity-1 text-[var(--category-color)]",
                item.color && "bg-opacity-1 text-[var(--item-color)]",
                activeFilter.title &&
                  item.title !== activeFilter.title &&
                  "text-gray-400",
              )}
            >
              {item.icon && React.createElement(item.icon)}
              {item.iconImage && (
                <img
                  src={item.iconImage}
                  alt={t(item.title)}
                  className="max-h-5 max-w-5 lg:max-h-8 lg:max-w-8"
                />
              )}
            </span>
            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm lg:text-base">
              {item.translatedTitle ? item.translatedTitle : t(item.title)}
            </p>
          </motion.li>
        );
      });
  };

  if (mobileMode) {
    return (
      <div
        className="pb-[50px]"
        style={{
          //@ts-expect-error color
          "--category-color": `hsl(${color})`,
          "--custom-handle-bg": `${color}`,
        }}
      >
        <h3 className="absolute top-5 mb-3 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-xl font-bold group-data-[dir=ltr]:left-5 group-data-[dir=rtl]:right-5 group-data-[dir=ltr]:text-left group-data-[dir=rtl]:text-right">
          {heading}
        </h3>
        <Button
          size="icon"
          onClick={closeHandler}
          className={cn(
            "absolute top-5 h-8 w-8 text-lg group-data-[dir=ltr]:right-5 group-data-[dir=rtl]:left-5",
          )}
        >
          <PiXBold />
        </Button>
        <OverlayScrollbarsComponent
          className={cn("max-h-[194px] overflow-auto")}
          options={{
            scrollbars: { autoHide: "move", autoHideDelay: 3000 },
            overflow: { x: "hidden" },
          }}
          defer
        >
          <ul className="mb-2 flex flex-col">{filterItems()}</ul>
        </OverlayScrollbarsComponent>
      </div>
    );
  }

  return (
    <>
      <AnimatePresence>
        <AccordionItem
          value={id}
          style={{
            //@ts-expect-error color
            "--category-color": `hsl(${color})`,
            "--custom-handle-bg": `${color}`,
          }}
          className={cn("rounded-xl border-0", isOpen && "bg-gray-100")}
        >
          <AccordionTrigger
            className={cn(
              "max-w-full px-5 text-2xl font-bold outline-none hover:text-primary hover:no-underline",
              color && "hover:text-[var(--category-color)]",
            )}
          >
            {category?.icon && (
              <span
                className={cn(
                  "text-2xl group-data-[dir=ltr]:mr-3 group-data-[dir=rtl]:ml-3",
                  color && "text-[var(--category-color)]",
                )}
              >
                {React.createElement(category.icon)}
              </span>
            )}
            {category?.iconImage && (
              <img
                src={category.iconImage}
                alt={t(category.title)}
                className={cn(
                  "w-[24px] group-data-[dir=ltr]:mr-3 group-data-[dir=rtl]:ml-3",
                )}
              />
            )}
            <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap capitalize group-data-[dir=ltr]:text-left group-data-[dir=rtl]:text-right">
              {heading}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <OverlayScrollbarsComponent
              className={cn("max-h-80 overflow-auto px-5")}
              options={{
                scrollbars: { autoHide: "move", autoHideDelay: 3000 },
                overflow: { x: "hidden" },
              }}
              defer
            >
              <ul className="mb-2 flex flex-col">{filterItems()}</ul>
            </OverlayScrollbarsComponent>
          </AccordionContent>
        </AccordionItem>
      </AnimatePresence>
    </>
  );
}

export default FilterPicker;
